import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import { presence as knownPresenceNames, durationKind as knownDurationNames } from './knownFacets';
const originalWindowTitle = document.title;
const instantsearchPrefix = 'i';
const seoPathSuffix = 'Volunteer Opportunities';

const buildQueryStringFromRouteState = (qsModule, routeState) => {
  const queryParameters = {};

  if (routeState?.query) {
    queryParameters.query = encodeURIComponent(routeState.query);
  }

  if (routeState?.page !== 1) {
    queryParameters.page = encodeURIComponent(routeState.page);
  }

  if (routeState?.area) {
    queryParameters.area = encodeURIComponent(routeState.area);
  }

  if (routeState?.aroundRadius) {
    queryParameters.aroundRadius = encodeURIComponent(routeState.aroundRadius);
  }

  if (routeState?.aroundLatLng) {
    queryParameters.aroundLatLng = encodeURIComponent(routeState.aroundLatLng);
  }

  if (routeState?.issueArea) {
    queryParameters.issueArea = routeState?.issueArea?.map(encodeURIComponent);
  }

  if (routeState?.skills) {
    queryParameters.skills = routeState?.skills?.map(encodeURIComponent);
  }

  return qsModule.stringify(queryParameters, {
    addQueryPrefix: true,
    arrayFormat: 'repeat',
  });
};

const getSeoPathSlug = (routeState) => {
  return [routeState.presence, routeState.durationKind]
    .filter(Boolean)
    .map(encodeURIComponent)
    .concat([seoPathSuffix.replace(' ', '+')])
    .join('+');
};

const getSeoPathNames = (seoPath) => {
  const names = seoPath.replace(seoPathSuffix, '').split('+').map(decodeURIComponent);

  return {
    presence: names.find((name) => knownPresenceNames.includes(name)),
    durationKind: names.find((name) => knownDurationNames.includes(name)),
  };
};

const withTrailingSlash = (string) => string.replace(/\/?$/, '/');

const router = historyRouter({
  windowTitle(routeState) {
    const prefix =
      routeState?.presence || routeState?.durationKind
        ? getSeoPathSlug(routeState).replaceAll('+', ' ')
        : '';
    const queryTitle = routeState.query ? `Results for "${routeState.query}"` : originalWindowTitle;

    return prefix ? `${prefix} - ${queryTitle}` : queryTitle;
  },

  createURL({ qsModule, routeState, location }) {
    const { protocol, hostname, port = '', pathname, hash } = location;
    const portWithPrefix = port === '' ? '' : `:${port}`;
    const urlParts = location.href.match(new RegExp(`^(.*?)\/${instantsearchPrefix}`));
    const baseUrl =
      (urlParts && urlParts[0]) ||
      `${protocol}//${hostname}${portWithPrefix}${withTrailingSlash(
        pathname
      )}${instantsearchPrefix}`;

    const queryString = buildQueryStringFromRouteState(qsModule, routeState);

    const seoPath =
      routeState?.presence || routeState?.durationKind ? getSeoPathSlug(routeState) : '';

    return `${baseUrl}/${seoPath}${queryString}${hash}`;
  },

  parseURL({ qsModule, location }) {
    const pathnameMatches = location.pathname.match(
      new RegExp(`^.*?\/${instantsearchPrefix}\/(.*?)\/?$`)
    );
    const { presence = '', durationKind = '' } = getSeoPathNames(pathnameMatches?.[1] || '');
    const queryParameters = qsModule.parse(location.search.slice(1));
    const {
      query = '',
      page = 1,
      area = '',
      aroundLatLng = '',
      aroundRadius = '',
      issueArea = '',
      skills = '',
    } = queryParameters;

    const allIssueAreas = Array.isArray(issueArea) ? issueArea : [issueArea].filter(Boolean);
    const allSkills = Array.isArray(skills) ? skills : [skills].filter(Boolean);

    return {
      query: decodeURIComponent(query),
      page: decodeURIComponent(page),
      presence: decodeURIComponent(presence),
      durationKind: decodeURIComponent(durationKind),
      area: decodeURIComponent(area),
      aroundLatLng: decodeURIComponent(aroundLatLng),
      aroundRadius: decodeURIComponent(aroundRadius),
      issueArea: allIssueAreas.map(decodeURIComponent),
      skills: allSkills.map(decodeURIComponent),
    };
  },
});

const getStateMapping = ({ indexName }) => ({
  stateToRoute(uiState) {
    const indexUiState = uiState[indexName];

    return {
      query: indexUiState?.query || '',
      page: indexUiState?.page || 1,
      presence: indexUiState?.menu?.presence || '',
      durationKind: indexUiState?.menu?.duration_kind || '',
      area: indexUiState?.places?.query || '',
      aroundLatLng: indexUiState?.places?.position || '',
      area: indexUiState?.googlePlaces?.query || '',
      aroundLatLng: indexUiState?.googlePlaces?.position || '',
      aroundRadius: indexUiState?.aroundRadius || '',
      issueArea: indexUiState?.refinementList?._tags || [],
      skills: indexUiState?.refinementList?.skills || [],
    };
  },

  routeToState(routeState) {
    return {
      [indexName]: {
        query: routeState.query,
        page: routeState.page,
        menu: {
          presence: routeState.presence,
          duration_kind: routeState.durationKind,
        },
        googlePlaces: {
          query: routeState.area,
          position: routeState.aroundLatLng,
        },
        refinementList: {
          _tags: routeState.issueArea,
          skills: routeState.skills,
        },
        aroundRadius: routeState.aroundRadius,
      },
    };
  },
});

const getRouting = ({ indexName }) => ({
  router,
  stateMapping: getStateMapping({ indexName }),
});

export default getRouting;
